import { css, keyframes } from '@emotion/css';

/**
 * El logo de Rumbo (la rosa de los vientos con la R) montándose: los cuatro
 * puntos cardinales llegan desde los bordes, se dibuja el arco, aparece la R y
 * la aguja da una vuelta hasta marcar el rumbo. `escala` acorta los tiempos
 * (1 = la primera vez; menos, las siguientes). Con «reducir movimiento» se ve
 * directamente el logo terminado.
 */
export function BrujulaAnimada({
  size = 132,
  escala = 1,
}: {
  size?: number;
  escala?: number;
}) {
  const s = (ms: number) => `${Math.round(ms * escala)}ms`;
  return (
    <svg
      viewBox="0 0 512 512"
      width={size}
      height={size}
      aria-hidden
      className={css({
        overflow: 'visible',
        '@media (prefers-reduced-motion: reduce)': {
          '& *': { animation: 'none !important' },
        },
      })}
    >
      <path
        className={css({
          strokeDasharray: 960,
          animation: `${traza} ${s(1200)} ease-out ${s(1200)} both`,
        })}
        d="M353.7 372.4 A152 152 0 1 1 396.9 312.9"
        fill="none"
        stroke="#2e8b62"
        strokeWidth="24"
        strokeLinecap="round"
      />
      {PUNTAS.map(({ giro, dx, dy, retraso }) => (
        <g
          key={giro}
          className={css({
            animation: `${llega(dx, dy)} ${s(1000)} cubic-bezier(.2,.8,.2,1) ${s(retraso)} both`,
          })}
        >
          <g transform={`rotate(${giro} 256 256)`}>
            <path d="M256 34 L238 106 L256 122 Z" fill="#dff3e8" />
            <path d="M256 34 L274 106 L256 122 Z" fill="#8fd3b0" />
          </g>
        </g>
      ))}
      <path
        className={css({
          animation: `${aparece} ${s(600)} ease-out ${s(1600)} both`,
        })}
        d="M172.6 339.4 L164.8 347.2 M172.6 172.6 L164.8 164.8 M339.4 172.6 L347.2 164.8"
        stroke="#2e8b62"
        strokeWidth="5"
        strokeLinecap="round"
      />
      <g
        className={css({
          transformBox: 'view-box',
          transformOrigin: '220.4px 275.3px',
          animation: `${gira} ${s(1800)} cubic-bezier(.3,1.6,.45,1) ${s(2100)} both`,
        })}
      >
        <path d="M170 345 L282 190 L202.6 262.4 Z" fill="#8fd3b0" />
        <path d="M170 345 L282 190 L238.2 288.1 Z" fill="#dff3e8" />
        <circle cx="220.4" cy="275.3" r="7" fill="#0f3b3a" />
      </g>
      <g
        className={css({
          animation: `${aparece} ${s(800)} ease-out ${s(1900)} both`,
        })}
      >
        <path
          d="M148 217 C184 185 240 163 292 163 C337 163 362 187 360 219 C358 252 318 278 256 288 L250 266 C300 258 322 240 324 218 C326 197 311 188 288 189 C246 190 196 201 148 217 Z"
          fill="#dff3e8"
        />
        <path
          d="M246 270 C296 270 320 298 338 326 C354 350 378 364 422 342 C394 384 338 388 310 354 C294 334 282 302 250 288 Z"
          fill="#dff3e8"
        />
      </g>
    </svg>
  );
}

/** Duración total de la animación del logo con escala 1 (ms). */
export const DURACION_BRUJULA = 3900;

const PUNTAS = [
  { giro: 0, dx: 0, dy: -260, retraso: 300 },
  { giro: 90, dx: 260, dy: 0, retraso: 450 },
  { giro: 180, dx: 0, dy: 260, retraso: 600 },
  { giro: 270, dx: -260, dy: 0, retraso: 750 },
];

function llega(dx: number, dy: number) {
  return keyframes({
    from: { opacity: 0, transform: `translate(${dx}px, ${dy}px)` },
    to: { opacity: 1, transform: 'translate(0, 0)' },
  });
}

const traza = keyframes({
  from: { strokeDashoffset: 960 },
  to: { strokeDashoffset: 0 },
});

const aparece = keyframes({
  from: { opacity: 0, transform: 'translateY(14px)' },
  to: { opacity: 1, transform: 'none' },
});

const gira = keyframes({
  from: { transform: 'rotate(-260deg)' },
  to: { transform: 'rotate(0deg)' },
});
